import { useRef } from 'react';
import { clampToViewport } from '@/lib/clampRect';

/**
 * Fixed-position card that can be dragged (via the built-in top grip) and
 * resized (bottom-end corner). The parent owns x/y/width/height and persists
 * them; this component only reports new values via onMove/onResize. Start
 * pos/size are captured in a ref on mousedown to avoid stale-closure drift
 * (same convention as DockResizeHandle).
 *
 * Props:
 *  - x, y, width, height : current geometry (px), parent-owned
 *  - onMove   : ({x,y}) => void
 *  - onResize : ({width,height}) => void
 *  - minW, minH : resize floors (default 300 / 220)
 *  - gripLabel  : aria-label for the drag grip
 *  - style, className : applied to the outer card
 *  - children : card body
 */
export default function DraggablePanel({
  x, y, width, height, onMove, onResize,
  minW = 300, minH = 220, gripLabel = 'Drag', style, className, children,
}) {
  const dragRef = useRef(null);
  const sizeRef = useRef(null);

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = { px: e.clientX, py: e.clientY, x, y };
    const move = (ev) => {
      const nx = dragRef.current.x + (ev.clientX - dragRef.current.px);
      const ny = dragRef.current.y + (ev.clientY - dragRef.current.py);
      onMove(clampToViewport({ x: nx, y: ny, w: width, h: height, vw: window.innerWidth, vh: window.innerHeight }));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sizeRef.current = { px: e.clientX, py: e.clientY, w: width, h: height };
    const move = (ev) => {
      const nw = Math.max(minW, sizeRef.current.w + (ev.clientX - sizeRef.current.px));
      const nh = Math.max(minH, sizeRef.current.h + (ev.clientY - sizeRef.current.py));
      onResize({ width: nw, height: nh });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div
      className={className}
      style={{ position: 'fixed', left: x, top: y, width, height, display: 'flex', flexDirection: 'column', ...style }}
    >
      {/* Inner wrapper clips content to the rounded corners; the resize handle
          sits OUTSIDE it (sibling) so an `overflow-hidden`-style clip can't
          swallow the corner affordance. */}
      <div className="flex-1 min-h-0 flex flex-col" style={{ borderRadius: 'inherit', overflow: 'hidden' }}>
        {/* drag grip */}
        <div
          onMouseDown={startDrag}
          role="button"
          aria-label={gripLabel}
          className="flex-shrink-0 flex items-center justify-center py-1.5"
          style={{ cursor: 'grab' }}
        >
          <span className="w-9 h-1 rounded-full bg-slate-300/80" />
        </div>
        {children}
      </div>
      {/* resize corner */}
      <div
        onMouseDown={startResize}
        aria-label="Resize"
        role="button"
        className="absolute bottom-0 end-0 w-4 h-4 z-[1]"
        style={{ cursor: 'nwse-resize' }}
      >
        <span className="absolute bottom-1 end-1 w-2 h-2 border-b-2 border-e-2 border-slate-400/70" />
      </div>
    </div>
  );
}
